import { useState } from 'react'
import { SortAsc, RefreshCw } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { usePosts, useApprovePost, usePublishPost, useSchedulePost, useDeletePost, useSyncEngagement } from './use-content'
import { useApi } from '@/hooks/use-api'
import { PostCard } from './post-card'
import { PostEditor } from './post-editor'
import { GeneratePostDialog } from './generate-post-dialog'
import type { GenerateDialogInitial } from './generate-post-dialog'
import { DmTriggerDialog } from './dm-trigger-dialog'
import { usePlan } from '@/hooks/use-plan'
import { PLAN_FEATURES } from '@dealcloser/shared'
import type { PostFilters, PostStatus, Post } from './types'
import { STATUS_LABELS } from './types'

const FILTER_TABS: Array<{ value: PostStatus | 'all'; label: string }> = [
  // No separate "Pending Review" tab: generated posts are stored as drafts
  // (the API has no pending_review status), so that tab duplicated Drafts.
  // "Publish Failed" was missing, so failed scheduled posts were only
  // visible under All.
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'approved', label: STATUS_LABELS.approved },
  { value: 'scheduled', label: STATUS_LABELS.scheduled },
  { value: 'published', label: STATUS_LABELS.published },
  { value: 'failed', label: STATUS_LABELS.failed },
]

const SORT_OPTIONS: Array<{ value: NonNullable<PostFilters['sort']>; label: string }> = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'scheduled', label: 'Scheduled date' },
]

function PostCardSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-4 w-32" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-7 w-20" />
        <Skeleton className="h-7 w-16" />
      </div>
    </div>
  )
}

type PostsListProps = {
  readonly showGenerateDialog?: boolean
  readonly onGenerateDialogClose?: () => void
}

export function PostsList({ showGenerateDialog = false, onGenerateDialogClose }: PostsListProps) {
  const [statusFilter, setStatusFilter] = useState<PostStatus | 'all'>('all')
  const [sort, setSort] = useState<NonNullable<PostFilters['sort']>>('newest')
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [publishingId, setPublishingId] = useState<string | null>(null)
  const [schedulingId, setSchedulingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [regenerate, setRegenerate] = useState<GenerateDialogInitial | null>(null)
  const [dmPost, setDmPost] = useState<Post | null>(null)
  const plan = usePlan()
  const canCommentDm = PLAN_FEATURES[plan]?.engagementToDm === true

  const { data: posts = [], isLoading } = usePosts({ status: statusFilter, sort })
  const { mutateAsync: approvePost } = useApprovePost()
  const { mutateAsync: publishPost } = usePublishPost()
  const { mutateAsync: schedulePost } = useSchedulePost()
  const { mutateAsync: deletePost } = useDeletePost()
  const { mutateAsync: syncEngagement, isPending: isSyncing } = useSyncEngagement()
  const apiFetch = useApi()

  async function handleApprove(id: string) {
    setApprovingId(id)
    try {
      await approvePost(id)
    } finally {
      setApprovingId(null)
    }
  }

  async function handlePublish(id: string) {
    setPublishingId(id)
    try {
      // Fetch the user's LinkedIn account ID via the status endpoint
      const res = await apiFetch<{ status: string; account: { unipile_account_id: string } | null }>(
        '/api/leads/linkedin/status',
      )
      const accountId = res.data?.account?.unipile_account_id
      if (!accountId || res.data?.status !== 'connected') {
        alert('No LinkedIn account connected. Please connect your LinkedIn account in Settings first.')
        return
      }
      await publishPost({ id, accountId })
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to publish post')
    } finally {
      setPublishingId(null)
    }
  }

  async function handleSchedule(id: string, scheduledAt: string) {
    setSchedulingId(id)
    try {
      await schedulePost({ id, scheduledAt })
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to schedule post')
    } finally {
      setSchedulingId(null)
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this post? This cannot be undone.')) return
    setDeletingId(id)
    try {
      await deletePost(id)
    } finally {
      setDeletingId(null)
    }
  }

  function handleRegenerate(post: Post) {
    // "Regenerate" used to open the plain editor. Now it opens the generate
    // flow prefilled with this post's topic/format/tone; the new draft is
    // reviewed before anything is scheduled.
    setRegenerate({
      topic: post.topic ?? '',
      ...(post.content_type ? { contentType: post.content_type } : {}),
      ...(post.tone ? { tone: post.tone } : {}),
    })
  }

  return (
    <>
      <Tabs
        defaultValue="all"
        value={statusFilter}
        onValueChange={(v) => setStatusFilter(v as PostStatus | 'all')}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList className="flex-wrap">
            {FILTER_TABS.map(({ value, label }) => (
              <TabsTrigger key={value} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex items-center gap-2">
            {/* Sort selector */}
            <div className="flex items-center gap-1.5">
              <SortAsc className="h-3.5 w-3.5 text-muted-foreground" />
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as NonNullable<PostFilters['sort']>)}
                className="h-8 rounded-md border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {SORT_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={() => void syncEngagement()}
              disabled={isSyncing}
              className="gap-1.5 h-8 text-xs"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* All tab values share the same content area */}
        {FILTER_TABS.map(({ value }) => (
          <TabsContent key={value} value={value} className="mt-4">
            {isLoading ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <PostCardSkeleton key={i} />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                <p className="text-sm font-medium text-muted-foreground">No posts found</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {statusFilter === 'all'
                    ? 'Use the Generate Post button at the top right to draft your first post.'
                    : `No ${STATUS_LABELS[statusFilter as PostStatus]?.toLowerCase()} posts yet.`}
                </p>
              </div>
            ) : (
              <div className={cn('grid gap-4', posts.length > 1 ? 'sm:grid-cols-2 xl:grid-cols-3' : '')}>
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onApprove={handleApprove}
                    onPublish={handlePublish}
                    onSchedule={handleSchedule}
                    onEdit={setEditingPost}
                    onRegenerate={handleRegenerate}
                    onDelete={handleDelete}
                    {...(canCommentDm ? { onDmTrigger: setDmPost } : {})}
                    isApproving={approvingId === post.id}
                    isPublishing={publishingId === post.id}
                    isScheduling={schedulingId === post.id}
                    isDeleting={deletingId === post.id}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Post editor dialog */}
      <PostEditor
        post={editingPost}
        open={editingPost !== null}
        onClose={() => setEditingPost(null)}
      />

      {/* Regenerate: generate flow prefilled from an existing draft */}
      <GeneratePostDialog
        open={regenerate !== null}
        initial={regenerate ?? undefined}
        onClose={() => setRegenerate(null)}
        onGenerated={() => setRegenerate(null)}
      />

      <DmTriggerDialog post={dmPost} onClose={() => setDmPost(null)} />

      {/* Generate dialog (controlled externally from ContentPage header) */}
      {onGenerateDialogClose && (
        <GeneratePostDialog
          open={showGenerateDialog}
          onClose={onGenerateDialogClose}
          onGenerated={() => {
            onGenerateDialogClose()
          }}
        />
      )}
    </>
  )
}
